"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.questionOpenApiDocs = void 0;
// Question payload schemas
const QuestionCreatePayload = {
    type: 'object',
    description: 'Question creation request parameters',
    properties: {
        title: {
            type: 'string',
            description: 'Question title - clear and concise question text',
            example: 'What is your primary area of expertise?',
            required: true
        },
        description: {
            type: 'string',
            description: 'Additional description or context for the question',
            example: 'Please select the field where you have the most experience and knowledge',
            required: false
        },
        type: {
            type: 'string',
            enum: ['text', 'textarea', 'select', 'radio', 'checkbox', 'number', 'email', 'url', 'date'],
            description: 'Question type that determines how the question is displayed and answered',
            example: 'select',
            required: true
        },
        options: {
            type: 'array',
            description: 'Available options for select, radio, or checkbox questions',
            items: {
                type: 'object',
                properties: {
                    value: {
                        type: 'string',
                        description: 'Option value'
                    },
                    label: {
                        type: 'string',
                        description: 'Option display label'
                    }
                },
                required: ['value', 'label']
            },
            example: [
                { value: 'tech', label: 'Technology' },
                { value: 'finance', label: 'Finance' },
                { value: 'healthcare', label: 'Healthcare' }
            ],
            required: false
        },
        required: {
            type: 'boolean',
            description: 'Whether this question must be answered',
            default: false,
            required: false
        },
        order: {
            type: 'integer',
            description: 'Display order of the question within its step',
            example: 1,
            minimum: 0,
            required: true
        },
        step_id: {
            type: 'string',
            description: 'ID of the step this question belongs to',
            example: '507f1f77bcf86cd799439011',
            required: true
        }
    },
    required: ['title', 'type', 'order', 'step_id']
};
const QuestionUpdatePayload = {
    type: 'object',
    description: 'Question update request parameters',
    properties: {
        title: {
            type: 'string',
            description: 'Updated question title',
            example: 'What is your primary area of professional expertise?',
            required: false
        },
        description: {
            type: 'string',
            description: 'Updated question description',
            example: 'Please select the field where you have the most professional experience',
            required: false
        },
        type: {
            type: 'string',
            enum: ['text', 'textarea', 'select', 'radio', 'checkbox', 'number', 'email', 'url', 'date'],
            description: 'Updated question type',
            required: false
        },
        options: {
            type: 'array',
            description: 'Updated options array',
            items: {
                type: 'object',
                properties: {
                    value: { type: 'string' },
                    label: { type: 'string' }
                }
            },
            required: false
        },
        required: {
            type: 'boolean',
            description: 'Updated required status',
            required: false
        },
        order: {
            type: 'integer',
            description: 'Updated display order',
            minimum: 0,
            required: false
        },
        step_id: {
            type: 'string',
            description: 'Updated step ID this question belongs to',
            required: false
        }
    }
};
const QuestionResponse = {
    id: {
        type: 'number',
        description: 'Unique question identifier'
    },
    title: {
        type: 'string',
        description: 'Question title'
    },
    description: {
        type: 'string',
        description: 'Question description'
    },
    type: {
        type: 'string',
        enum: ['text', 'textarea', 'select', 'radio', 'checkbox', 'number', 'email', 'url', 'date'],
        description: 'Question type'
    },
    options: {
        type: 'array',
        description: 'Available options for select/radio/checkbox questions',
        items: {
            type: 'object',
            properties: {
                value: { type: 'string' },
                label: { type: 'string' }
            }
        }
    },
    required: {
        type: 'boolean',
        description: 'Whether the question is required'
    },
    order: {
        type: 'integer',
        description: 'Display order within the step'
    },
    step: {
        type: 'object',
        description: 'Associated step information',
        properties: {
            id: { type: 'number' },
            title: { type: 'string' },
            description: { type: 'string' }
        }
    },
    created_at: {
        type: 'string',
        format: 'date-time',
        description: 'Creation timestamp'
    },
    updated_at: {
        type: 'string',
        format: 'date-time',
        description: 'Last update timestamp'
    }
};
const QuestionListResponse = {
    success: {
        type: 'boolean'
    },
    data: {
        type: 'array',
        items: {
            type: 'object',
            properties: QuestionResponse
        }
    },
    count: {
        type: 'integer',
        description: 'Total number of questions'
    }
};
exports.questionOpenApiDocs = {
    'question.list': {
        description: 'List all questions with optional filtering by step or search query',
        responses: {
            200: {
                description: 'Questions retrieved successfully',
                returnParams: QuestionListResponse
            },
            500: {
                description: 'Internal server error - failed to retrieve questions',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5303
                    }
                }
            }
        }
    },
    'question.create': {
        description: 'Create a new question with specified type and options',
        payload: {
            body: QuestionCreatePayload
        },
        responses: {
            200: {
                description: 'Question created successfully',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    data: {
                        type: 'object',
                        properties: QuestionResponse
                    }
                }
            },
            400: {
                description: 'Bad request - validation error or missing required fields',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 9005
                    }
                }
            },
            404: {
                description: 'Step not found for the specified step_id',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5306
                    }
                }
            },
            500: {
                description: 'Internal server error - question creation failed',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5304
                    }
                }
            }
        }
    },
    'question.get': {
        description: 'Get question by ID with associated step information',
        responses: {
            200: {
                description: 'Question retrieved successfully',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    data: {
                        type: 'object',
                        properties: QuestionResponse
                    }
                }
            },
            404: {
                description: 'Question not found',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5301
                    }
                }
            },
            500: {
                description: 'Internal server error',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5303
                    }
                }
            }
        }
    },
    'question.update': {
        description: 'Update an existing question\'s properties and configuration',
        payload: {
            body: QuestionUpdatePayload
        },
        responses: {
            200: {
                description: 'Question updated successfully',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    data: {
                        type: 'object',
                        properties: QuestionResponse
                    }
                }
            },
            400: {
                description: 'Bad request - validation error or invalid parameters',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 9005
                    }
                }
            },
            404: {
                description: 'Question not found or associated step not found',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5301
                    }
                }
            },
            500: {
                description: 'Internal server error - question update failed',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5304
                    }
                }
            }
        }
    },
    'question.delete': {
        description: 'Delete question by ID and all associated answers',
        responses: {
            200: {
                description: 'Question deleted successfully',
                returnParams: {
                    success: {
                        type: 'boolean'
                    }
                }
            },
            404: {
                description: 'Question not found',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5301
                    }
                }
            },
            500: {
                description: 'Internal server error - question deletion failed',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5305
                    }
                }
            }
        }
    }
};
